import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, fmtDate, todayYmd, inr } from "@/lib/api";
import { DISTRICT_OPTIONS, INDIAN_STATES, OUTSIDE_KERALA } from "@/lib/geo";
import { PageHeader, StageBadge } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmployeePicker from "@/components/app/EmployeePicker";
import {
  Plus, ArrowRight, Search, LayoutGrid, List as ListIcon, ChevronUp, ChevronDown,
  Calendar as CalIcon, Phone, MessageSquare, UserCog, Flame, Bell, ArrowUpRight,
  Download, FileSpreadsheet, FileText, Activity as ActivityIcon, Edit3, ArrowLeftRight,
} from "lucide-react";
import { toast } from "sonner";
import RowDeleteButton from "@/components/app/RowDeleteButton";

const STAGES = ["New Lead", "Contacted", "Site Survey Scheduled", "Proposal Sent", "Negotiation", "Won", "Lost"];
const STATUSES = ["New Lead", "Contacted", "Site Survey Scheduled", "Site Survey Completed", "Proposal Sent", "Negotiation", "Won", "Lost", "Dormant"];
const SOURCES = ["Website", "Facebook", "Google Ads", "WhatsApp", "Referral", "Employee Referral"];
const TEMPS = ["Super Hot", "Hot", "Warm", "Cold"];
const PRIORITIES = ["High", "Medium", "Low"];
const TEMP_COLOR = {
  "Super Hot": "bg-[#DC2626] text-white",
  "Hot": "bg-[#F97316] text-white",
  "Warm": "bg-[#F59E0B] text-white",
  "Cold": "bg-[#3B82F6] text-white",
};
const PRIORITY_COLOR = {
  High: "bg-[#DC2626] text-white",
  Medium: "bg-[#F59E0B] text-white",
  Low: "bg-[#3B82F6] text-white",
};

function todayStr() { return todayYmd(); }
function isOverdue(l, today) {
  const s = l.status || l.stage;
  if (s === "Won" || s === "Lost") return false;
  return l.next_followup_date && l.next_followup_date < today;
}
function isToday(l, today) {
  const s = l.status || l.stage;
  if (s === "Won" || s === "Lost") return false;
  return l.next_followup_date === today;
}

export default function Leads() {
  const { state } = useLocation();
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(state?.view || "list");
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  const [drawerLead, setDrawerLead] = useState(null);
  // Each view fetches its own scoped data (paged list, follow-up endpoints,
  // kanban summary). Bumping this token re-fetches the active view after
  // creates/edits.
  const [reloadToken, setReloadToken] = useState(0);
  const reloadAll = () => setReloadToken((t) => t + 1);

  useEffect(() => {
    api.get("/users").then((r) => setEmployees((r.data || []).filter((u) => u.role !== "customer")));
  }, []);

  // Quick drawer: fetch the full lead only when opened.
  useEffect(() => {
    if (!drawerLeadId) { setDrawerLead(null); return; }
    api.get(`/leads/${drawerLeadId}`).then((r) => setDrawerLead(r.data)).catch(() => setDrawerLeadId(null));
  }, [drawerLeadId]);

  return (
    <div data-testid="leads-page">
      <PageHeader
        eyebrow="Customer Relationship Management"
        title="Sales Pipeline"
        subtitle="Track every lead from first touch to signed contract. Filter, follow up, and close fast."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="new-lead-button">
                <Plus className="w-4 h-4 mr-2" /> New Lead
              </Button>
            </DialogTrigger>
            {/* Remount each time the dialog opens so form state resets cleanly. */}
            {open && <NewLeadDialog onDone={() => { setOpen(false); reloadAll(); }} />}
          </Dialog>
        }
      />

      <div className="px-4 sm:px-8 pt-6 flex flex-wrap gap-2 border-b border-[#E5E7EB]">
        <TabBtn active={view === "list"} onClick={() => setView("list")} icon={ListIcon} label="List" testid="view-list" />
        <TabBtn active={view === "today"} onClick={() => setView("today")} icon={Bell} label="Today's Follow-Ups" testid="view-today" />
        <TabBtn active={view === "calendar"} onClick={() => setView("calendar")} icon={CalIcon} label="7-Day Calendar" testid="view-calendar" />
        <TabBtn active={view === "kanban"} onClick={() => setView("kanban")} icon={LayoutGrid} label="Kanban" testid="view-kanban" />
        <TabBtn active={view === "activity"} onClick={() => setView("activity")} icon={ActivityIcon} label="Activity Log" testid="view-activity" />
      </div>

      {view === "list" && (
        <ListView employees={employees} onReload={reloadAll} onQuick={setDrawerLeadId} reloadToken={reloadToken} />
      )}
      {view === "today" && (
        <TodayView onReload={reloadAll} onQuick={setDrawerLeadId} reloadToken={reloadToken} />
      )}
      {view === "calendar" && (
        <CalendarView onReload={reloadAll} reloadToken={reloadToken} />
      )}
      {view === "kanban" && (
        <KanbanView onReload={reloadAll} reloadToken={reloadToken} />
      )}
      {view === "activity" && (
        <ActivityLogView onQuick={setDrawerLeadId} />
      )}

      <Dialog open={!!drawerLeadId} onOpenChange={(o) => !o && setDrawerLeadId(null)}>
        {drawerLead && (
          <QuickActionDrawer
            lead={drawerLead}
            employees={employees}
            onDone={() => { setDrawerLeadId(null); reloadAll(); }}
          />
        )}
      </Dialog>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 -mb-px border-b-2 flex items-center gap-2 text-xs uppercase tracking-widest font-bold transition-colors ${active ? "border-[#0a0a0a] text-[#0a0a0a]" : "border-transparent text-[#9CA3AF] hover:text-[#0a0a0a]"}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function TempBadge({ value }) {
  if (!value) return <span className="text-[#9CA3AF] text-xs">—</span>;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${TEMP_COLOR[value] || "bg-[#6B7280] text-white"}`} data-testid={`temp-badge-${value}`}><Flame className="w-3 h-3" />{value}</span>;
}
function PriorityBadge({ value }) {
  if (!value) return <span className="text-[#9CA3AF] text-xs">—</span>;
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${PRIORITY_COLOR[value]}`}>{value}</span>;
}

// =====================================================================
//  LIST VIEW with filters + sortable columns + quick actions
// =====================================================================
function ListView({ employees, onReload, onQuick, reloadToken }) {
  const today = todayStr();
  const [filters, setFilters] = useState({
    q: "", assignee: "all", source: "all", status: "all", temperature: "all",
    fuBucket: "all", minBill: "", maxBill: "", minKw: "", maxKw: "",
    district: "all", state: "all",
  });
  const [sort, setSort] = useState({ col: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  // Debounce free-text search so each keystroke doesn't hit the API.
  const [qLive, setQLive] = useState("");
  useEffect(() => {
    const t = setTimeout(() => { setFilters((f) => ({ ...f, q: qLive })); setPage(1); }, 220);
    return () => clearTimeout(t);
  }, [qLive]);

  const setF = (k) => (v) => { setFilters({ ...filters, [k]: v }); setPage(1); };

  // Fetch paged data whenever filters / sort / page / pageSize change.
  useEffect(() => {
    const params = new URLSearchParams({
      paged: "1",
      limit: String(pageSize),
      skip: String((page - 1) * pageSize),
      sort_col: sort.col,
      sort_dir: sort.dir,
    });
    if (filters.q.trim()) params.set("q", filters.q.trim());
    if (filters.assignee !== "all") params.set("assignee", filters.assignee);
    if (filters.source !== "all") params.set("source", filters.source);
    if (filters.status !== "all") params.set("status", filters.status);
    if (filters.temperature !== "all") params.set("temperature", filters.temperature);
    if (filters.district !== "all") params.set("district", filters.district);
    if (filters.state !== "all") params.set("state", filters.state);
    if (filters.fuBucket !== "all") params.set("fu_bucket", filters.fuBucket);
    if (filters.minBill !== "") params.set("min_bill", filters.minBill);
    if (filters.maxBill !== "") params.set("max_bill", filters.maxBill);
    if (filters.minKw !== "") params.set("min_kw", filters.minKw);
    if (filters.maxKw !== "") params.set("max_kw", filters.maxKw);
    setLoading(true);
    api.get(`/leads?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [filters, sort, page, pageSize, reloadToken]);

  const filtered = data.items;    // server-filtered already
  const paged = data.items;       // server-paged already
  const safePage = page;
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const toggleSort = (col) => { setSort((s) => s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }); setPage(1); };

  const isDefaultFilters = (
    !filters.q.trim() && filters.assignee === "all" && filters.source === "all" &&
    filters.status === "all" && filters.temperature === "all" && filters.fuBucket === "all" &&
    filters.minBill === "" && filters.maxBill === "" && filters.minKw === "" && filters.maxKw === "" &&
    filters.district === "all" && filters.state === "all"
  );

  const COLS = [
    { key: "name", testid: "lead-name", label: "Lead Name" },
    { key: "phone", testid: "phone", label: "Phone" },
    { key: "source", testid: "source", label: "Source" },
    { key: "monthly_bill_inr", testid: "monthly-bill", label: "Monthly Bill" },
    { key: "capacity_kw", testid: "capacity", label: "Inverter (kW)" },
    { key: "panel_capacity_kw", testid: "panel-capacity", label: "Panel (kW)" },
    { key: "temperature", testid: "temp", label: "Temp" },
    { key: "status", testid: "status", label: "Status" },
    { key: "assignee_name", testid: "assignee", label: "Assignee" },
    { key: "next_followup_date", testid: "next-fu-date", label: "Next FU" },
    { key: "next_followup_time", testid: "next-fu-time", label: "Time" },
    { key: "updated_at", testid: "last-activity", label: "Last Activity" },
    { key: "created_at", testid: "created", label: "Created" },
    { key: "_delete", testid: "delete", label: "", noSort: true },
  ];

  return (
    <div className="px-4 sm:px-8 py-6 space-y-4">
      {/* Filter bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <div className="relative col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={qLive} onChange={(e) => setQLive(e.target.value)} placeholder="Search name, phone, email, address…" className="rounded-none h-10 pl-9" data-testid="leads-search-input" />
        </div>
        <FilterSelect value={filters.assignee} onChange={setF("assignee")} placeholder="All Assignees" options={[{ value: "all", label: "All Assignees" }, ...employees.map((e) => ({ value: e.id, label: e.name }))]} testid="filter-assignee" />
        <FilterSelect value={filters.source} onChange={setF("source")} placeholder="All Sources" options={[{ value: "all", label: "All Sources" }, ...SOURCES.map((v) => ({ value: v, label: v }))]} testid="filter-source" />
        <FilterSelect value={filters.status} onChange={setF("status")} placeholder="All Statuses" options={[{ value: "all", label: "All Statuses" }, ...STATUSES.map((v) => ({ value: v, label: v }))]} testid="filter-status" />
        <FilterSelect value={filters.temperature} onChange={setF("temperature")} placeholder="All Temps" options={[{ value: "all", label: "All Temps" }, ...TEMPS.map((v) => ({ value: v, label: v }))]} testid="filter-temp" />
        <FilterSelect value={filters.district} onChange={setF("district")} placeholder="All Districts" options={[{ value: "all", label: "All Districts" }, ...DISTRICT_OPTIONS.map((v) => ({ value: v, label: v }))]} testid="filter-district" />
        {filters.district === "Outside Kerala" && (
          <FilterSelect value={filters.state} onChange={setF("state")} placeholder="All States" options={[{ value: "all", label: "All States" }, ...INDIAN_STATES.filter((s) => s !== "Kerala").map((v) => ({ value: v, label: v }))]} testid="filter-state" />
        )}
        <FilterSelect value={filters.fuBucket} onChange={setF("fuBucket")} placeholder="Follow-Up" options={[
          { value: "all", label: "All Follow-Ups" }, { value: "today", label: "Due Today" }, { value: "week", label: "Due This Week" }, { value: "overdue", label: "Overdue" }, { value: "none", label: "No Follow-Up Date" },
        ]} testid="filter-fu" />
        <div className="flex gap-1">
          <Input type="number" value={filters.minBill} onChange={(e) => setF("minBill")(e.target.value)} placeholder="Bill ≥" className="rounded-none h-10" data-testid="filter-min-bill" />
          <Input type="number" value={filters.maxBill} onChange={(e) => setF("maxBill")(e.target.value)} placeholder="≤" className="rounded-none h-10" data-testid="filter-max-bill" />
        </div>
        <div className="flex gap-1">
          <Input type="number" value={filters.minKw} onChange={(e) => setF("minKw")(e.target.value)} placeholder="kW ≥" className="rounded-none h-10" data-testid="filter-min-kw" />
          <Input type="number" value={filters.maxKw} onChange={(e) => setF("maxKw")(e.target.value)} placeholder="≤" className="rounded-none h-10" data-testid="filter-max-kw" />
        </div>
      </div>

      <div className="text-xs text-[#4B5563] flex flex-wrap items-center gap-3 justify-between">
        <div>Showing <b>{paged.length}</b> of <b>{data.total}</b> matched {loading && <span className="text-[10px] text-[#9CA3AF]">(loading…)</span>}</div>
        <div className="flex items-center gap-3">
          <LeadsExportMenu
            exportParams={{
              q: filters.q, assignee: filters.assignee, source: filters.source,
              status: filters.status, temperature: filters.temperature,
              fu_bucket: filters.fuBucket, district: filters.district,
              state: filters.state, min_bill: filters.minBill, max_bill: filters.maxBill,
              min_kw: filters.minKw, max_kw: filters.maxKw,
            }}
            totalCount={data.total}
            filtered={!isDefaultFilters}
            disabled={data.total === 0}
          />
          <div className="flex items-center gap-2">
            <span className="label-eyebrow !text-[9px]">Rows</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="rounded-none h-8 w-20" data-testid="page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr>
              {COLS.map((c) => (
                <th key={c.key} className="text-left label-eyebrow !text-[9px] px-3 py-3 cursor-pointer select-none whitespace-nowrap"
                    onClick={() => !c.noSort && toggleSort(c.key)} data-testid={c.noSort ? undefined : `sort-${c.testid}`}>
                  <span className="flex items-center gap-1">
                    {c.label}
                    {!c.noSort && sort.col === c.key && (sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((l) => {
              const overdue = isOverdue(l, today);
              return (
                <tr key={l.id} className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] ${overdue ? "bg-[#FEF2F2]" : ""}`} data-testid={`lead-list-row-${l.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/leads/${l.id}`} className="font-semibold hover:underline">{l.name}</Link>
                    <div className="flex items-center gap-2 mt-0.5">
                      <button onClick={() => onQuick(l.id)} className="text-[10px] font-bold text-[#002FA7] hover:underline uppercase tracking-widest" data-testid={`quick-action-${l.id}`}>Quick</button>
                      <span className="text-[#E5E7EB]">|</span>
                      <Link to={`/leads/${l.id}`} className="text-[10px] font-bold text-[#0a0a0a] hover:underline uppercase tracking-widest" data-testid={`lead-open-${l.id}`}>Open</Link>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">{l.phone}</td>
                  <td className="px-3 py-2 text-xs">{l.source}</td>
                  <td className="px-3 py-2 text-xs">{l.monthly_bill_inr ? inr(l.monthly_bill_inr) : "—"}</td>
                  <td className="px-3 py-2 text-xs">{l.capacity_kw ? `${l.capacity_kw} kW` : "—"}</td>
                  <td className="px-3 py-2 text-xs">{l.panel_capacity_kw ? `${l.panel_capacity_kw} kW` : "—"}</td>
                  <td className="px-3 py-2"><TempBadge value={l.temperature} /></td>
                  <td className="px-3 py-2"><StageBadge stage={l.status || l.stage} /></td>
                  <td className="px-3 py-2 text-xs">{l.assignee_name || <span className="text-[#9CA3AF]">—</span>}</td>
                  <td className={`px-3 py-2 text-xs whitespace-nowrap ${overdue ? "text-[#DC2626] font-bold" : ""}`}>{l.next_followup_date || "—"}</td>
                  <td className="px-3 py-2 text-xs">{l.next_followup_time || "—"}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(l.updated_at)}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(l.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <RowDeleteButton
                      endpoint={`/leads/${l.id}`}
                      confirmMessage={`Delete lead "${l.name}"? This cannot be undone.`}
                      onDeleted={onReload}
                      testid={`lead-delete-${l.id}`}
                      successMessage="Lead deleted"
                    />
                  </td>
                </tr>
              );
            })}
            {paged.length === 0 && <tr><td colSpan={13} className="text-center text-sm text-[#9CA3AF] py-12">No leads match your filters.</td></tr>}
          </tbody>
        </table>
      </div>

      {data.total > 0 && (
        <Pagination
          page={safePage}
          totalPages={totalPages}
          onPage={setPage}
          pageStart={(safePage - 1) * pageSize}
          pageCount={paged.length}
          totalCount={data.total}
        />
      )}
    </div>
  );
}

function Pagination({ page, totalPages, onPage, pageStart, pageCount, totalCount }) {
  if (totalPages <= 1) {
    return <div className="text-xs text-[#4B5563] text-center" data-testid="pagination-info">Showing {pageCount} of {totalCount}</div>;
  }
  const pageButtons = [];
  const maxBtns = 7;
  let start = Math.max(1, page - 3);
  let end = Math.min(totalPages, start + maxBtns - 1);
  if (end - start + 1 < maxBtns) start = Math.max(1, end - maxBtns + 1);
  for (let p = start; p <= end; p++) pageButtons.push(p);

  return (
    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="pagination">
      <div className="text-xs text-[#4B5563]">Showing <b>{pageStart + 1}</b>–<b>{pageStart + pageCount}</b> of <b>{totalCount}</b></div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(1)} disabled={page === 1} className="px-2 py-1 border border-[#0a0a0a] text-[10px] uppercase font-bold tracking-widest disabled:opacity-40" data-testid="page-first">First</button>
        <button onClick={() => onPage(page - 1)} disabled={page === 1} className="px-2 py-1 border border-[#0a0a0a] text-[10px] uppercase font-bold tracking-widest disabled:opacity-40" data-testid="page-prev">‹ Prev</button>
        {start > 1 && <span className="text-xs px-1">…</span>}
        {pageButtons.map((p) => (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={`min-w-[28px] px-2 py-1 text-[11px] font-bold ${p === page ? "bg-[#0a0a0a] text-white" : "border border-[#0a0a0a] hover:bg-[#F9FAFB]"}`}
            data-testid={`page-${p}`}
          >
            {p}
          </button>
        ))}
        {end < totalPages && <span className="text-xs px-1">…</span>}
        <button onClick={() => onPage(page + 1)} disabled={page === totalPages} className="px-2 py-1 border border-[#0a0a0a] text-[10px] uppercase font-bold tracking-widest disabled:opacity-40" data-testid="page-next">Next ›</button>
        <button onClick={() => onPage(totalPages)} disabled={page === totalPages} className="px-2 py-1 border border-[#0a0a0a] text-[10px] uppercase font-bold tracking-widest disabled:opacity-40" data-testid="page-last">Last</button>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-none h-10" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// =====================================================================
//  TODAY'S FOLLOW-UPS view
// =====================================================================
function TodayView({ onReload, onQuick, reloadToken }) {
  const today = todayStr();
  // Scoped fetch: only today's + overdue follow-ups, not the whole lead book.
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/leads/followups/today"), api.get("/leads/followups/overdue")])
      .then(([a, b]) => {
        const seen = new Set();
        setLeads([...(a.data || []), ...(b.data || [])].filter((l) => !seen.has(l.id) && seen.add(l.id)));
      })
      .catch(() => setLeads([]));
  }, [reloadToken]);
  const items = leads.filter((l) => isToday(l, today) || isOverdue(l, today))
    .sort((a, b) => (a.next_followup_date || "").localeCompare(b.next_followup_date || ""));
  return (
    <div className="px-4 sm:px-8 py-6">
      <div className="mb-4 flex items-center gap-3">
        <h2 className="font-black text-2xl tracking-tight">Today's Follow-Ups</h2>
        <span className="text-xs text-[#4B5563]">{items.length} pending {items.length > 0 && `(${items.filter((l) => isOverdue(l, today)).length} overdue)`}</span>
      </div>
      <div className="border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr>{["Lead Name", "Phone", "Assignee", "Status", "Priority", "Date", "Time", "Actions"].map((h) => (
              <th key={h} className="text-left label-eyebrow !text-[9px] px-3 py-3 whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {items.map((l) => {
              const overdue = isOverdue(l, today);
              return (
                <tr key={l.id} className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] ${overdue ? "bg-[#FEF2F2]" : ""}`} data-testid={`today-row-${l.id}`}>
                  <td className="px-3 py-2 font-semibold"><Link to={`/leads/${l.id}`} className="hover:underline">{l.name}</Link></td>
                  <td className="px-3 py-2 text-xs font-mono">{l.phone}</td>
                  <td className="px-3 py-2 text-xs">{l.assignee_name || "—"}</td>
                  <td className="px-3 py-2"><StageBadge stage={l.status || l.stage} /></td>
                  <td className="px-3 py-2"><PriorityBadge value={l.followup_priority} /></td>
                  <td className={`px-3 py-2 text-xs ${overdue ? "text-[#DC2626] font-bold" : ""}`}>{l.next_followup_date || "—"}{overdue && " (overdue)"}</td>
                  <td className="px-3 py-2 text-xs">{l.next_followup_time || "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {l.phone && <a href={`tel:${l.phone}`} className="text-[10px] font-bold text-[#10B981] hover:underline uppercase tracking-widest" data-testid={`today-call-${l.id}`}>Call</a>}
                    <span className="text-[#E5E7EB] mx-1.5">|</span>
                    <button onClick={() => onQuick(l.id)} className="text-[10px] font-bold text-[#002FA7] hover:underline uppercase tracking-widest" data-testid={`today-quick-${l.id}`}>Update</button>
                    <span className="text-[#E5E7EB] mx-1.5">|</span>
                    <Link to={`/leads/${l.id}`} className="text-[10px] font-bold text-[#0a0a0a] hover:underline uppercase tracking-widest">Open</Link>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={8} className="text-center text-sm text-[#9CA3AF] py-12">Nothing on today's plate. Take a coffee break ☕</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================
//  7-DAY CALENDAR view
// =====================================================================
function CalendarView({ onReload, reloadToken }) {
  // Scoped fetch: follow-ups in the displayed 7-day window only.
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    api.get("/leads/followups/week").then((r) => setLeads(r.data || [])).catch(() => setLeads([]));
  }, [reloadToken]);
  const today = new Date();
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today); d.setDate(today.getDate() + i);
    return { iso: todayYmd(d), label: d.toLocaleDateString(undefined, { weekday: "short" }), num: d.getDate(), isToday: i === 0 };
  });

  const byDay = (iso) => leads.filter((l) => {
    const s = l.status || l.stage;
    if (s === "Won" || s === "Lost") return false;
    return l.next_followup_date === iso;
  });

  const onDragStart = (e, leadId) => {
    e.dataTransfer.setData("text/lead-id", leadId);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = async (e, iso) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/lead-id");
    if (!id) return;
    try {
      await api.patch(`/leads/${id}`, { next_followup_date: iso });
      toast.success(`Rescheduled to ${iso}`);
      onReload();
    } catch (err) { toast.error("Reschedule failed"); }
  };

  return (
    <div className="px-4 sm:px-8 py-6">
      <h2 className="font-black text-2xl tracking-tight mb-1">7-Day Follow-Up Calendar</h2>
      <p className="text-xs text-[#4B5563] mb-4">Drag any card to reschedule. Color = priority (red=High, orange=Med, blue=Low).</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
        {days.map((d) => {
          const items = byDay(d.iso);
          return (
            <div key={d.iso} className={`border ${d.isToday ? "border-[#0a0a0a] bg-[#FFFBEB]" : "border-[#E5E7EB] bg-white"} min-h-[280px] flex flex-col`}
                 onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, d.iso)} data-testid={`cal-col-${d.iso}`}>
              <div className={`border-b border-[#E5E7EB] px-3 py-2 ${d.isToday ? "bg-[#0a0a0a] text-white" : "bg-[#F9FAFB]"}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold">{d.label}{d.isToday && " · Today"}</div>
                <div className="text-2xl font-black tracking-tight">{d.num}</div>
              </div>
              <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                {items.map((l) => {
                  const color = l.followup_priority === "High" ? "border-l-[#DC2626] bg-[#FEF2F2]"
                              : l.followup_priority === "Medium" ? "border-l-[#F59E0B] bg-[#FFFBEB]"
                              : l.followup_priority === "Low" ? "border-l-[#3B82F6] bg-[#EFF6FF]"
                              : "border-l-[#9CA3AF] bg-white";
                  return (
                    <div
                      key={l.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, l.id)}
                      className={`border border-[#E5E7EB] border-l-4 ${color} p-2 cursor-grab active:cursor-grabbing`}
                      data-testid={`cal-event-${l.id}`}
                    >
                      <Link to={`/leads/${l.id}`} className="block">
                        <div className="font-bold text-sm leading-tight">{l.name}</div>
                        <div className="text-[11px] font-mono text-[#4B5563]">{l.phone}</div>
                        <div className="text-[11px] text-[#4B5563] mt-1 flex items-center justify-between">
                          <span>{l.next_followup_time || "—"}</span>
                          <span className="text-[#0a0a0a] font-semibold">{l.assignee_name || "—"}</span>
                        </div>
                      </Link>
                    </div>
                  );
                })}
                {items.length === 0 && <div className="text-[10px] text-[#9CA3AF] italic py-4 text-center">Drop here</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =====================================================================
//  KANBAN view (legacy, retained as an option)
// =====================================================================
function KanbanView({ onReload, reloadToken }) {
  // Slim summary payload (pipeline-card fields only), not full lead docs.
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    api.get("/leads?summary=1").then((r) => setLeads(r.data || [])).catch(() => setLeads([]));
  }, [reloadToken]);
  const byStage = (s) => leads.filter((l) => (l.stage || l.status) === s);
  const moveStage = async (id, stage) => {
    await api.post(`/leads/${id}/stage`, { stage });
    toast.success(`Moved to ${stage}`); onReload();
  };
  return (
    <div className="overflow-x-auto">
      <div className="flex gap-0 min-w-max px-4 sm:px-8 py-6">
        {STAGES.map((stage) => (
          <div key={stage} className="w-72 flex-shrink-0 bg-[#F9FAFB] border-r border-[#E5E7EB] p-4" data-testid={`pipeline-column-${stage.replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="label-eyebrow">{stage}</div>
              <div className="text-xs font-bold bg-white border border-[#E5E7EB] px-2 py-0.5">{byStage(stage).length}</div>
            </div>
            <div className="space-y-3">
              {byStage(stage).map((l) => {
                const idx = STAGES.indexOf(l.stage); const next = idx >= 0 && idx < STAGES.length - 2 ? STAGES[idx + 1] : null;
                return (
                  <div key={l.id} className="bg-white border border-[#E5E7EB] p-3 hover:border-[#0a0a0a] transition-colors" data-testid={`lead-card-${l.id}`}>
                    <Link to={`/leads/${l.id}`} className="block">
                      <div className="text-sm font-bold text-[#0a0a0a] tracking-tight">{l.name}</div>
                      <div className="text-xs text-[#4B5563]">{l.phone}</div>
                    </Link>
                    <div className="flex items-center gap-2 mt-2">
                      <TempBadge value={l.temperature} />
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F3F4F6]">
                      <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">{l.source} · {l.capacity_kw ? `${l.capacity_kw} kW` : "—"}</div>
                      {next && (
                        <button onClick={() => moveStage(l.id, next)} className="text-[10px] font-bold text-[#002FA7] hover:underline flex items-center gap-1" data-testid={`lead-advance-${l.id}`}>
                          Next <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {byStage(stage).length === 0 && <div className="text-xs text-[#9CA3AF] italic py-6 text-center border border-dashed border-[#E5E7EB]">Empty</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
//  QUICK ACTION DRAWER
// =====================================================================
function QuickActionDrawer({ lead, employees, onDone }) {
  const [busy, setBusy] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [form, setForm] = useState({
    last_followup_notes: "",
    next_followup_date: lead.next_followup_date || "",
    next_followup_time: lead.next_followup_time || "",
    followup_priority: lead.followup_priority || "Medium",
    status: lead.status || lead.stage || "New Lead",
    temperature: lead.temperature || "",
    assignee_id: lead.assignee_id || "",
    call_outcome: "Not Reachable", call_duration: 5, call_notes: "",
    note: "",
  });
  const set = (k) => (v) => setForm({ ...form, [k]: v.target ? v.target.value : v });

  const submitFollowup = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/log-followup`, {
        last_followup_notes: form.last_followup_notes,
        next_followup_date: form.next_followup_date || null,
        next_followup_time: form.next_followup_time || null,
        followup_priority: form.followup_priority,
        status: form.status,
      });
      toast.success("Follow-up logged");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const submitCall = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/calls`, { outcome: form.call_outcome, duration_min: Number(form.call_duration), note: form.call_notes });
      if (data?.auto_advanced_stage) {
        toast.success(`Call logged · Stage → ${data.auto_advanced_stage}`);
      } else {
        toast.success("Call logged");
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const submitNote = async () => {
    setBusy(true);
    try {
      const newNotes = `${lead.notes || ""}${lead.notes ? "\n\n" : ""}[${new Date().toLocaleString()}] ${form.note}`;
      await api.patch(`/leads/${lead.id}`, { notes: newNotes });
      toast.success("Note added");
      onDone();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const submitStatus = async () => {
    setBusy(true);
    try {
      await api.patch(`/leads/${lead.id}`, { status: form.status, temperature: form.temperature });
      toast.success("Updated"); onDone();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const submitReassign = async () => {
    setBusy(true);
    try {
      const u = employees.find((e) => e.id === form.assignee_id);
      await api.patch(`/leads/${lead.id}`, { assignee_id: u?.id || null, assignee_name: u?.name || null });
      toast.success(`Reassigned to ${u?.name || "nobody"}`); onDone();
    } catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const TABS = [
    { id: "followup", label: "Schedule Follow-Up", icon: CalIcon },
    { id: "call", label: "Log Call", icon: Phone },
    { id: "note", label: "Add Note", icon: MessageSquare },
    { id: "status", label: "Change Status", icon: ArrowUpRight },
    { id: "reassign", label: "Reassign", icon: UserCog },
  ];

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="quick-drawer">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight">{lead.name}</DialogTitle>
        <div className="text-xs text-[#4B5563] flex flex-wrap gap-x-3 gap-y-1">
          <span>{lead.phone}</span>
          <span>· {lead.source}</span>
          <span>· {lead.assignee_name || "Unassigned"}</span>
          <button
            onClick={() => setReassignOpen((o) => !o)}
            className="text-[#002FA7] hover:underline uppercase tracking-widest font-bold text-[10px] ml-auto"
            data-testid="quick-toggle-reassign"
          >
            <UserCog className="w-3 h-3 inline mr-1" /> {reassignOpen ? "Hide Reassign" : "Reassign…"}
          </button>
        </div>
      </DialogHeader>

      <div className="pt-3 space-y-5">
        {reassignOpen && (
          <Section title="Reassign" testid="qa-section-reassign">
            <Label className="label-eyebrow">New Assignee</Label>
            <div className="mt-2">
              <EmployeePicker
                employees={employees}
                value={form.assignee_id}
                onChange={(id) => set("assignee_id")(id)}
                placeholder="Pick a user…"
                testid="qa-ra-assignee"
              />
            </div>
            <Button onClick={submitReassign} disabled={busy} className="mt-2 w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-10 uppercase tracking-widest text-xs font-bold" data-testid="qa-ra-submit">
              {busy ? "Saving…" : "Reassign"}
            </Button>
          </Section>
        )}

        <Section title="Schedule Follow-Up" icon={CalIcon} testid="qa-section-followup">
          <div>
            <Label className="label-eyebrow">Last Follow-Up Notes</Label>
            <Textarea value={form.last_followup_notes} onChange={set("last_followup_notes")} placeholder="What happened in this conversation?" className="rounded-none mt-2" rows={2} data-testid="qa-fu-notes" />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <Label className="label-eyebrow">Next Date</Label>
              <Input type="date" value={form.next_followup_date} onChange={set("next_followup_date")} className="rounded-none mt-2 h-10" data-testid="qa-fu-date" />
            </div>
            <div>
              <Label className="label-eyebrow">Next Time</Label>
              <Input type="time" value={form.next_followup_time} onChange={set("next_followup_time")} className="rounded-none mt-2 h-10" data-testid="qa-fu-time" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <Label className="label-eyebrow">Priority</Label>
              <Select value={form.followup_priority} onValueChange={set("followup_priority")}>
                <SelectTrigger className="rounded-none mt-2 h-10" data-testid="qa-fu-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="rounded-none mt-2 h-10" data-testid="qa-fu-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {["Won", "Lost"].includes(form.status) && <div className="text-[10px] text-[#F59E0B] mt-1">Won/Lost will clear the next follow-up automatically.</div>}
          <Button onClick={submitFollowup} disabled={busy} className="mt-3 w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-10 uppercase tracking-widest text-xs font-bold" data-testid="qa-fu-submit">
            {busy ? "Saving…" : "Log Follow-Up"}
          </Button>
        </Section>

        <Section title="Log Call" icon={Phone} testid="qa-section-call">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow">Outcome</Label>
              <Select value={form.call_outcome} onValueChange={set("call_outcome")}>
                <SelectTrigger className="rounded-none mt-2 h-10" data-testid="qa-call-outcome"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{["Not Reachable","Switched Off","Wrong Number","Call Back Later","Connected - Highly Interested","Connected - Interested","Connected - Need More Information","Connected - Comparing Options","Connected - Not Interested","Site Survey Scheduled","Proposal Sent","Negotiation Ongoing","Token Payment Received","Order Confirmed","Closed Won","Closed Lost"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Duration (min)</Label>
              <Input type="number" value={form.call_duration} onChange={set("call_duration")} className="rounded-none mt-2 h-10" data-testid="qa-call-duration" />
            </div>
          </div>
          <div className="mt-2">
            <Label className="label-eyebrow">Notes</Label>
            <Textarea value={form.call_notes} onChange={set("call_notes")} className="rounded-none mt-2" rows={2} data-testid="qa-call-notes" />
          </div>
          <Button onClick={submitCall} disabled={busy} className="mt-3 w-full rounded-none bg-[#10B981] hover:bg-[#059669] h-10 uppercase tracking-widest text-xs font-bold" data-testid="qa-call-submit">
            {busy ? "Saving…" : "Log Call"}
          </Button>
        </Section>

        <Section title="Add Note" icon={MessageSquare} testid="qa-section-note">
          <Textarea value={form.note} onChange={set("note")} placeholder="Quick note…" className="rounded-none" rows={3} data-testid="qa-note-text" />
          <Button onClick={submitNote} disabled={busy || !form.note.trim()} className="mt-3 w-full rounded-none bg-[#0a0a0a] hover:bg-black h-10 uppercase tracking-widest text-xs font-bold" data-testid="qa-note-submit">
            {busy ? "Saving…" : "Append Note"}
          </Button>
        </Section>

        <Section title="Change Status" icon={ArrowUpRight} testid="qa-section-status">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger className="rounded-none mt-2 h-10" data-testid="qa-st-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Temperature</Label>
              <Select value={form.temperature || "_none"} onValueChange={(v) => set("temperature")(v === "_none" ? "" : v)}>
                <SelectTrigger className="rounded-none mt-2 h-10" data-testid="qa-st-temp"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">— None —</SelectItem>{TEMPS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={submitStatus} disabled={busy} className="mt-3 w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-10 uppercase tracking-widest text-xs font-bold" data-testid="qa-st-submit">
            {busy ? "Saving…" : "Update Status"}
          </Button>
        </Section>
      </div>
    </DialogContent>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  return (
    <section className="border border-[#E5E7EB] p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 label-eyebrow !text-[10px] text-[#0a0a0a] pb-2 border-b border-[#F3F4F6]">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      <div className="pt-2">{children}</div>
    </section>
  );
}

// =====================================================================
//  EXPORT MENU — dropdown with CSV / XLSX / PDF
// =====================================================================
function LeadsExportMenu({ exportParams, totalCount, filtered, disabled }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  const doExport = (fmt) => {
    if (disabled || !totalCount) { toast.error("No leads to export"); return; }
    const url = new URL("/api/leads/export", window.location.origin);
    url.searchParams.set("fmt", fmt);
    Object.entries(exportParams || {}).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined && v !== "all") url.searchParams.set(k, v);
    });
    if (filtered) url.searchParams.set("filtered", "1");
    const a = document.createElement("a");
    a.href = url.pathname + url.search;
    a.click();
    setOpen(false);
    toast.success(`Exporting ${totalCount} lead${totalCount === 1 ? "" : "s"} as ${fmt.toUpperCase()}`);
  };

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <Button
        onClick={() => setOpen(!open)}
        variant="outline"
        size="sm"
        className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8 px-3"
        disabled={disabled}
        data-testid="leads-export-btn"
      >
        <Download className="w-3.5 h-3.5 mr-1.5" /> Export {filtered ? "Filtered" : "All"}
      </Button>
      {open && (
        <div className="absolute right-0 top-9 z-30 border border-[#0a0a0a] bg-white shadow-lg min-w-[160px]" data-testid="leads-export-menu">
          <button onClick={() => doExport("xlsx")} className="w-full text-left px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-[#F9FAFB] flex items-center gap-2" data-testid="leads-export-xlsx">
            <FileSpreadsheet className="w-4 h-4 text-[#10B981]" /> Excel (.xlsx)
          </button>
          <button onClick={() => doExport("csv")} className="w-full text-left px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-[#F9FAFB] flex items-center gap-2" data-testid="leads-export-csv">
            <FileSpreadsheet className="w-4 h-4" /> CSV (.csv)
          </button>
          <button onClick={() => doExport("pdf")} className="w-full text-left px-4 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-[#F9FAFB] flex items-center gap-2" data-testid="leads-export-pdf">
            <FileText className="w-4 h-4 text-[#DC2626]" /> PDF (.pdf)
          </button>
        </div>
      )}
    </div>
  );
}

// =====================================================================
//  NEW LEAD dialog (with new fields)
// =====================================================================
function NewLeadDialog({ onDone }) {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", source: "Website", referral_name: "", referral_user_id: "",
    capacity_kw: "", panel_capacity_kw: "", address: "", map_link: "", consumer_number: "", transformer_kva: "",
    billing_cycle: "Monthly", consumption_kwh: "", bill_inr: "",
    district: "", state: "Kerala", outside_kerala_city: "",
    notes: "", stage: "New Lead", status: "New Lead",
    temperature: "", assignee_id: "", assignee_name: "",
    next_followup_date: "", next_followup_time: "", followup_priority: "Medium",
  });
  useEffect(() => { api.get("/users").then((r) => setEmployees((r.data || []).filter((u) => u.role !== "customer"))); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const setAssignee = (uid) => {
    if (uid === "_none") { setForm({ ...form, assignee_id: "", assignee_name: "" }); return; }
    const u = employees.find((x) => x.id === uid);
    setForm({ ...form, assignee_id: uid, assignee_name: u?.name || "" });
  };

  const submit = async () => {
    if (!form.district) { toast.error("District is required"); return; }
    if (form.district === OUTSIDE_KERALA) {
      if (!form.state) { toast.error("State is required for leads outside Kerala"); return; }
      if (!form.outside_kerala_city?.trim()) { toast.error("District / City is required"); return; }
    }
    if (form.source === "Referral" && !form.referral_name?.trim()) {
      toast.error("Referral name is required when Source is Referral"); return;
    }
    if (form.source === "Employee Referral" && !form.referral_user_id) {
      toast.error("Select the referring employee"); return;
    }
    try {
      const cycKwh = form.consumption_kwh ? parseFloat(form.consumption_kwh) : null;
      const cycBill = form.bill_inr ? parseFloat(form.bill_inr) : null;
      const divider = form.billing_cycle === "Bi-Monthly" ? 2 : 1;
      const monthlyKwh = cycKwh != null ? cycKwh / divider : null;
      const monthlyBill = cycBill != null ? cycBill / divider : null;
      const { consumption_kwh, bill_inr, ...rest } = form;
      const payload = {
        ...rest,
        capacity_kw: form.capacity_kw ? parseFloat(form.capacity_kw) : null,
        panel_capacity_kw: form.panel_capacity_kw ? parseFloat(form.panel_capacity_kw) : null,
        transformer_kva: form.transformer_kva ? parseFloat(form.transformer_kva) : null,
        billing_cycle: form.billing_cycle || "Monthly",
        billing_cycle_consumption_kwh: cycKwh,
        billing_cycle_bill_inr: cycBill,
        monthly_consumption_kwh: monthlyKwh,
        monthly_bill_inr: monthlyBill,
        district: form.district,
        state: form.district === OUTSIDE_KERALA ? form.state : "Kerala",
        outside_kerala_city: form.district === OUTSIDE_KERALA ? form.outside_kerala_city.trim() : null,
        referral_name: ["Referral", "Employee Referral"].includes(form.source) ? (form.referral_name || "").trim() : null,
        referral_user_id: form.source === "Employee Referral" ? form.referral_user_id : null,
        temperature: form.temperature || null,
        next_followup_date: form.next_followup_date || null,
        next_followup_time: form.next_followup_time || null,
        followup_priority: form.followup_priority || null,
      };
      await api.post("/leads", payload);
      toast.success("Lead created");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-lead-dialog">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">Capture New Lead</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <Field label="Full Name" value={form.name} onChange={set("name")} testid="lead-name" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone" value={form.phone} onChange={set("phone")} testid="lead-phone" />
          <Field label="Email" value={form.email} onChange={set("email")} testid="lead-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Source</Label>
            <Select value={form.source} onValueChange={set("source")}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="lead-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field label="Inverter Capacity (kW)" type="number" value={form.capacity_kw} onChange={set("capacity_kw")} testid="lead-capacity-kw" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Panel Capacity (kW)" type="number" value={form.panel_capacity_kw} onChange={set("panel_capacity_kw")} testid="lead-panel-capacity-kw" />
          <div />
        </div>
        {form.source === "Referral" && (
          <Field
            label="Referred By *"
            value={form.referral_name}
            onChange={set("referral_name")}
            testid="lead-referral-name"
            placeholder="Name of the person who referred this lead"
          />
        )}
        {form.source === "Employee Referral" && (
          <div>
            <Label className="label-eyebrow">Referred By (Employee) *</Label>
            <div className="mt-2">
              <EmployeePicker
                employees={employees}
                value={form.referral_user_id}
                onChange={(id, u) => setForm({ ...form, referral_user_id: id || "", referral_name: u?.name || "" })}
                placeholder="Search employee…"
                testid="lead-referral-employee"
                allowUnassigned={false}
              />
            </div>
          </div>
        )}
        <Field label="Address" value={form.address} onChange={set("address")} testid="lead-address" />

        {/* Location classification */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="label-eyebrow">District *</Label>
            <Select value={form.district} onValueChange={(v) => setForm({ ...form, district: v, state: v === OUTSIDE_KERALA ? "" : "Kerala", outside_kerala_city: v === OUTSIDE_KERALA ? form.outside_kerala_city : "" })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="lead-district"><SelectValue placeholder="Select District" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {DISTRICT_OPTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.district === OUTSIDE_KERALA && (
            <>
              <div>
                <Label className="label-eyebrow">State *</Label>
                <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                  <SelectTrigger className="rounded-none mt-2 h-10" data-testid="lead-state"><SelectValue placeholder="Select State" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {INDIAN_STATES.filter((s) => s !== "Kerala").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field label="District / City *" value={form.outside_kerala_city} onChange={set("outside_kerala_city")} testid="lead-outside-city" placeholder="e.g. Coimbatore, Mangalore, Bengaluru Rural" />
            </>
          )}
        </div>

        <div>
          <Label className="label-eyebrow">Google Maps Link</Label>
          <Input value={form.map_link} onChange={set("map_link")} placeholder="Paste link from WhatsApp" className="rounded-none mt-2 h-10" data-testid="lead-map-link" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Consumer Number" value={form.consumer_number} onChange={set("consumer_number")} testid="lead-consumer-number" />
          <Field label="Transformer (kVA)" type="number" value={form.transformer_kva} onChange={set("transformer_kva")} testid="lead-transformer-kva" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="label-eyebrow">Billing Cycle</Label>
            <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="lead-billing-cycle"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Monthly">Monthly</SelectItem>
                <SelectItem value="Bi-Monthly">Bi-Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field label={`${form.billing_cycle} Consumption (kWh)`} type="number" value={form.consumption_kwh} onChange={set("consumption_kwh")} testid="lead-consumption" />
          <Field label={`${form.billing_cycle} Bill (₹)`} type="number" value={form.bill_inr} onChange={set("bill_inr")} testid="lead-bill" />
        </div>
        {form.consumption_kwh && form.billing_cycle === "Bi-Monthly" && (
          <div className="text-[10px] text-[#4B5563] -mt-1 italic">
            Equivalent Monthly: ~{Math.round(parseFloat(form.consumption_kwh) / 2)} kWh
            {form.bill_inr && <> · ~₹{Math.round(parseFloat(form.bill_inr) / 2).toLocaleString("en-IN")}/mo</>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v, stage: STAGES.includes(v) ? v : form.stage })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="lead-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Temperature</Label>
            <Select value={form.temperature || "_none"} onValueChange={(v) => set("temperature")(v === "_none" ? "" : v)}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="lead-temperature"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="_none">— None —</SelectItem>{TEMPS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="label-eyebrow">Assignee</Label>
          <div className="mt-2">
            <EmployeePicker
              employees={employees}
              value={form.assignee_id}
              onChange={(id) => setAssignee(id || "_none")}
              placeholder="Pick a user…"
              testid="lead-assignee"
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Next FU Date" type="date" value={form.next_followup_date} onChange={set("next_followup_date")} testid="lead-next-fu-date" />
          <Field label="Time" type="time" value={form.next_followup_time} onChange={set("next_followup_time")} testid="lead-next-fu-time" />
          <div>
            <Label className="label-eyebrow">Priority</Label>
            <Select value={form.followup_priority} onValueChange={set("followup_priority")}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="lead-fu-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="label-eyebrow">Notes</Label>
          <Textarea value={form.notes} onChange={set("notes")} className="rounded-none mt-2" rows={3} data-testid="lead-notes" />
        </div>
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="lead-submit">Create Lead</Button>
      </div>
    </DialogContent>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input type={type} value={value} onChange={onChange} placeholder={placeholder} className="rounded-none mt-2 h-10" data-testid={testid} />
    </div>
  );
}

// =====================================================================
//  ACTIVITY LOG VIEW — every call/follow-up/edit across all leads, filterable by date
// =====================================================================
const ACTIVITY_TYPES = [
  { v: "all", label: "All Activity" },
  { v: "call_log", label: "Call Logs / Quick Notes" },
  { v: "follow_up", label: "Follow-Up Logs" },
  { v: "status_change", label: "Status Changes" },
  { v: "notes_update", label: "Lead Notes Updates" },
  { v: "temperature_change", label: "Temperature Changes" },
  { v: "assignee_change", label: "Assignee Changes" },
  { v: "priority_change", label: "Priority Changes" },
];

const ACTIVITY_META = {
  call_log:           { label: "Call",        icon: Phone,         color: "bg-[#DBEAFE] text-[#1D4ED8]" },
  follow_up:          { label: "Follow-Up",   icon: Bell,          color: "bg-[#FEF3C7] text-[#92400E]" },
  status_change:      { label: "Status",      icon: ArrowLeftRight, color: "bg-[#E0E7FF] text-[#3730A3]" },
  notes_update:       { label: "Notes",       icon: Edit3,         color: "bg-[#F3F4F6] text-[#374151]" },
  temperature_change: { label: "Temperature", icon: Flame,         color: "bg-[#FFE4E6] text-[#9F1239]" },
  assignee_change:    { label: "Assignee",    icon: UserCog,       color: "bg-[#D1FAE5] text-[#065F46]" },
  priority_change:    { label: "Priority",    icon: ChevronUp,     color: "bg-[#FCE7F3] text-[#9D174D]" },
};

function ActivityLogView({ onQuick }) {
  const [date, setDate] = useState(todayStr());
  const [typeFilter, setTypeFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    api
      .get(`/lead-activities${params.toString() ? `?${params.toString()}` : ""}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [date]);

  const filtered = useMemo(
    () => (typeFilter === "all" ? items : items.filter((e) => e.type === typeFilter)),
    [items, typeFilter]
  );

  // Counts per type for chip badges
  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const it of items) c[it.type] = (c[it.type] || 0) + 1;
    return c;
  }, [items]);

  const fmtAt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // Always show India Standard Time so the log matches "when it happened" for the team.
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Kolkata",
    });
  };

  return (
    <div className="p-4 sm:p-8 space-y-4" data-testid="activity-log-view">
      {/* Filter bar */}
      <div className="flat-card p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="label-eyebrow">Date</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-none mt-2 h-10 w-44"
            data-testid="activity-date-input"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ACTIVITY_TYPES.map((t) => {
            const count = counts[t.v] ?? 0;
            const active = typeFilter === t.v;
            return (
              <button
                key={t.v}
                onClick={() => setTypeFilter(t.v)}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border ${
                  active
                    ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                    : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#0a0a0a]"
                }`}
                data-testid={`activity-type-${t.v}`}
              >
                {t.label} <span className={`ml-1 ${active ? "text-[#FCD34D]" : "text-[#9CA3AF]"}`}>({count})</span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex gap-2">
          <Button
            onClick={() => setDate(todayStr())}
            variant="outline"
            className="rounded-none text-[10px] uppercase tracking-widest font-bold h-10"
            data-testid="activity-today-btn"
          >
            Today
          </Button>
          <Button
            onClick={load}
            variant="outline"
            className="rounded-none text-[10px] uppercase tracking-widest font-bold h-10"
            data-testid="activity-refresh-btn"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-sm text-[#9CA3AF] py-16">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-sm text-[#9CA3AF] py-16 border border-dashed border-[#E5E7EB]" data-testid="activity-empty">
          No activity logged on {fmtDate(date) || date}.
        </div>
      ) : (
        <div className="border border-[#E5E7EB] divide-y divide-[#F3F4F6]" data-testid="activity-list">
          {filtered.map((e) => (
            <ActivityRow key={e.id} e={e} fmtAt={fmtAt} onQuick={onQuick} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityRow({ e, fmtAt, onQuick }) {
  const meta = ACTIVITY_META[e.type] || { label: e.type, icon: ActivityIcon, color: "bg-[#F3F4F6] text-[#374151]" };
  const Icon = meta.icon;
  const ex = e.extra || {};
  const desc = describeActivity(e);
  return (
    <div className="px-4 py-3 grid grid-cols-12 gap-3 hover:bg-[#F9FAFB]" data-testid={`activity-row-${e.id}`}>
      <div className="col-span-2 text-xs">
        <div className="font-mono font-bold text-[#0a0a0a]">{fmtAt(e.at)}</div>
        <span className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold ${meta.color}`}>
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
      </div>
      <div className="col-span-3">
        <button
          onClick={() => onQuick && onQuick(e.lead_id)}
          className="text-sm font-bold tracking-tight hover:underline text-left"
          data-testid={`activity-lead-btn-${e.id}`}
        >
          {e.lead_name || "Unknown lead"}
        </button>
        <div className="text-[10px] text-[#4B5563] flex items-center gap-1 mt-0.5">
          <Phone className="w-2.5 h-2.5" /> {e.lead_phone || "—"}
        </div>
      </div>
      <div className="col-span-5 text-xs text-[#374151]">{desc}</div>
      <div className="col-span-2 text-right">
        <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">By</div>
        <div className="text-xs font-bold">{e.by || "System"}</div>
        {ex.outcome && (
          <div className="text-[9px] uppercase tracking-widest text-[#3730A3] mt-0.5">{ex.outcome}</div>
        )}
      </div>
    </div>
  );
}

function describeActivity(e) {
  const ex = e.extra || {};
  switch (e.type) {
    case "call_log": {
      const bits = [];
      if (e.notes) bits.push(`"${e.notes}"`);
      if (ex.duration_min) bits.push(`${ex.duration_min} min`);
      if (ex.auto_advanced_stage) bits.push(`→ ${ex.auto_advanced_stage}`);
      return bits.length ? bits.join(" · ") : "Call logged";
    }
    case "follow_up": {
      const bits = [];
      if (e.notes) bits.push(`"${e.notes}"`);
      if (ex.status_after) bits.push(`Status: ${ex.status_after}`);
      if (ex.next_date) bits.push(`Next: ${ex.next_date}${ex.next_time ? ` · ${ex.next_time}` : ""}`);
      if (ex.priority) bits.push(`Priority: ${ex.priority}`);
      return bits.length ? bits.join(" · ") : "Follow-up logged";
    }
    case "status_change":
      return (
        <span>
          Status: <b className="text-[#9CA3AF]">{ex.old || "—"}</b>{" "}
          <ArrowRight className="inline w-3 h-3 mx-0.5" />{" "}
          <b className="text-[#0a0a0a]">{ex.new || "—"}</b>
        </span>
      );
    case "notes_update":
      return (
        <div>
          <div className="text-[#9CA3AF] line-through truncate">{ex.old || "(empty)"}</div>
          <div className="text-[#0a0a0a]">{ex.new || "(cleared)"}</div>
        </div>
      );
    case "temperature_change":
      return (
        <span>
          Temperature: <b className="text-[#9CA3AF]">{ex.old || "—"}</b>{" "}
          <ArrowRight className="inline w-3 h-3 mx-0.5" />{" "}
          <b className="text-[#0a0a0a]">{ex.new || "—"}</b>
        </span>
      );
    case "assignee_change":
      return (
        <span>
          Assignee: <b className="text-[#9CA3AF]">{ex.old || "—"}</b>{" "}
          <ArrowRight className="inline w-3 h-3 mx-0.5" />{" "}
          <b className="text-[#0a0a0a]">{ex.new || "—"}</b>
        </span>
      );
    case "priority_change":
      return (
        <span>
          Priority: <b className="text-[#9CA3AF]">{ex.old || "—"}</b>{" "}
          <ArrowRight className="inline w-3 h-3 mx-0.5" />{" "}
          <b className="text-[#0a0a0a]">{ex.new || "—"}</b>
        </span>
      );
    default:
      return e.notes || "—";
  }
}
